/**
 * NewTicketModal - Create a new ticket in AutoTask
 *
 * Allows users to create tickets with:
 * - Company selection (required)
 * - Title (required, can be AI-generated)
 * - Description (optional)
 * - Priority selection
 * - Assigned technician (optional)
 */

import { useState, useEffect, useRef } from 'react';
import { createTicketInAutoTask, getActiveCompanies, getResources, CompanyOption } from '../services/api';
import { Resource } from '../services/types';

interface NewTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketCreated?: (ticketNumber: string) => void;
}

// Priority options matching AutoTask
const PRIORITIES = [
  { id: 4, label: 'Critical', color: 'bg-red-500/20 text-red-400' },
  { id: 3, label: 'High', color: 'bg-orange-500/20 text-orange-400' },
  { id: 2, label: 'Medium', color: 'bg-yellow-500/20 text-yellow-400' },
  { id: 1, label: 'Low', color: 'bg-slate-500/20 text-slate-400' },
];

export default function NewTicketModal({ isOpen, onClose, onTicketCreated }: NewTicketModalProps) {
  // Form state
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(2); // Default: Medium
  const [assignedResourceId, setAssignedResourceId] = useState<number | null>(null);

  // Data state
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [generatingTitle, setGeneratingTitle] = useState(false);

  const companyInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load companies and resources on mount
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCompanyDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function loadData() {
    setLoadingData(true);
    try {
      const [companiesData, resourcesData] = await Promise.all([
        getActiveCompanies(),
        getResources(),
      ]);

      // Sort companies alphabetically
      setCompanies(companiesData.sort((a, b) => a.companyName.localeCompare(b.companyName)));

      // Filter to active technicians only
      const techs = resourcesData.filter(r =>
        r.isActive &&
        r.licenseType !== 3 &&
        r.autoTaskId !== 4 &&
        !(`${r.firstName} ${r.lastName}`.toLowerCase().includes('api'))
      );
      setResources(techs);
    } catch (err) {
      console.error('Failed to load form data:', err);
      setError('Failed to load companies and resources');
    } finally {
      setLoadingData(false);
    }
  }

  // Filter companies based on search
  const filteredCompanies = companies.filter(c =>
    c.companyName.toLowerCase().includes(companySearch.toLowerCase())
  );

  // Get selected company name
  const selectedCompany = companies.find(c => c.autoTaskId === companyId);

  // Generate title from description using AI
  async function handleGenerateTitle() {
    if (!description.trim()) {
      setError('Please enter a description first');
      return;
    }

    setGeneratingTitle(true);
    setError(null);

    try {
      const response = await fetch('/api/generate-title', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: description.trim() }),
      });

      const data = await response.json();

      if (data.success && data.title) {
        setTitle(data.title);
      } else {
        setError('Failed to generate title');
      }
    } catch (err) {
      console.error('Error generating title:', err);
      setError('Failed to generate title');
    } finally {
      setGeneratingTitle(false);
    }
  }

  // Submit the form
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Validation
    if (!companyId) {
      setError('Please select a company');
      return;
    }
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const ticketData: Parameters<typeof createTicketInAutoTask>[0] = {
        companyID: companyId,
        title: title.trim(),
        priority,
      };

      if (description.trim()) {
        ticketData.description = description.trim();
      }

      if (assignedResourceId) {
        ticketData.assignedResourceID = assignedResourceId;
        // When assigning, set status to In Progress (8)
        ticketData.status = 8;
      }

      const result = await createTicketInAutoTask(ticketData);

      setSuccess(`Ticket ${result.ticketNumber} created successfully!`);

      // Notify parent
      if (onTicketCreated) {
        onTicketCreated(result.ticketNumber);
      }

      // Reset form after short delay
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error creating ticket:', err);
      setError(err instanceof Error ? err.message : 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setCompanyId(null);
    setCompanySearch('');
    setTitle('');
    setDescription('');
    setPriority(2);
    setAssignedResourceId(null);
    setError(null);
    setSuccess(null);
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Ticket
          </h2>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loadingData ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-slate-400">Loading...</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Company Selection */}
              <div ref={dropdownRef}>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Company <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <input
                    ref={companyInputRef}
                    type="text"
                    value={showCompanyDropdown ? companySearch : (selectedCompany?.companyName || '')}
                    onChange={(e) => {
                      setCompanySearch(e.target.value);
                      setShowCompanyDropdown(true);
                      if (!e.target.value) {
                        setCompanyId(null);
                      }
                    }}
                    onFocus={() => {
                      setShowCompanyDropdown(true);
                      setCompanySearch('');
                    }}
                    placeholder="Search for a company..."
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                  />
                  {selectedCompany && !showCompanyDropdown && (
                    <button
                      type="button"
                      onClick={() => {
                        setCompanyId(null);
                        setCompanySearch('');
                        companyInputRef.current?.focus();
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Company Dropdown */}
                {showCompanyDropdown && (
                  <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-slate-700 border border-slate-600 rounded-lg shadow-lg">
                    {filteredCompanies.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-slate-400">No companies found</div>
                    ) : (
                      filteredCompanies.slice(0, 50).map(company => (
                        <button
                          key={company.autoTaskId}
                          type="button"
                          onClick={() => {
                            setCompanyId(company.autoTaskId);
                            setCompanySearch('');
                            setShowCompanyDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-600 transition-colors ${
                            companyId === company.autoTaskId ? 'bg-blue-600/30 text-blue-300' : 'text-white'
                          }`}
                        >
                          {company.companyName}
                        </button>
                      ))
                    )}
                    {filteredCompanies.length > 50 && (
                      <div className="px-3 py-2 text-xs text-slate-400 border-t border-slate-600">
                        Showing first 50 results. Type to narrow search.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Title <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter ticket title..."
                    maxLength={255}
                    className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateTitle}
                    disabled={generatingTitle || !description.trim()}
                    className="px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors flex items-center gap-1"
                    title="Generate title from description using AI"
                  >
                    {generatingTitle ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    )}
                    AI
                  </button>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Enter a title or write a description first and click AI to generate one
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the issue or request..."
                  rows={4}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:border-blue-500 focus:outline-none resize-none"
                />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Priority
                </label>
                <div className="flex gap-2">
                  {PRIORITIES.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPriority(p.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        priority === p.id
                          ? p.color + ' ring-2 ring-offset-2 ring-offset-slate-800 ring-blue-500'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Assigned Technician */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Assign To
                </label>
                <select
                  value={assignedResourceId || ''}
                  onChange={(e) => setAssignedResourceId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                >
                  <option value="">Unassigned (goes to queue)</option>
                  {resources.map(r => (
                    <option key={r.autoTaskId} value={r.autoTaskId}>
                      {r.firstName} {r.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Error/Success Messages */}
              {error && (
                <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 text-sm">
                  {success}
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700 bg-slate-800/50">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || loadingData || !companyId || !title.trim()}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Ticket
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
